import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import getAllEmployeesAndChildAPI from '../../../api/services/Scheduling/getAllEmployeesAndChild.service';
import getAllUserAndGroupNamesAPI from '../../../api/services/Scheduling/getAllUserAndGroupNames.service';
import getClinicianAndTechAPI from '../../../api/services/Scheduling/getClinicianAndTech.service';
import getEventsByProvidersAPI from '../../../api/services/Scheduling/getEventsByProviders.service';
import getScheduleEventAPI from '../../../api/services/Scheduling/getScheduleEvent.service';
import getTechnicianAvailabilityAPI from '../../../api/services/Scheduling/getTechnicianAvailability.service';
interface ScheduledEvent {
  searchUserId: string;
  // other properties
}

// ── Helper functions (extracted to reduce cognitive complexity) ──────────────

function getUserIdentifier(item: any): any {
  return item.id || item?.groupId || item?.childId || item?.parentId;
}

function findMatchingSearchedUser(users: any[], payload: any): any {
  return users.find(
    (item: any) =>
      getUserIdentifier(item) === getUserIdentifier(payload) && item?.type === payload?.type
  );
}

function applySearchedUserUpdate(user: any, payload: any): any {
  if (user.id !== payload.id && user.parentId !== payload.parentId) return user;
  return {
    ...user,
    colorCode: user.codeColor ? user.colorCode : payload?.colorCode,
    group: payload.group,
  };
}

function applyGroupUserUpdate(user: any, payload: any): any {
  if (user.id !== payload.id) return user;
  return {
    ...user,
    type: payload.type,
    colorCode: user.codeColor ? user.colorCode : payload?.colorCode,
    roleNames: payload?.roleNames || user.roleNames,
    group: payload.group,
  };
}

function mergeItemIntoUserMap(userMap: Map<any, any>, item: any): void {
  if (!userMap.has(item.id)) {
    userMap.set(item.id, { ...item, roleNames: item.roleName ? [item.roleName] : [] });
    return;
  }
  const existingUser = userMap.get(item.id);
  const existingRoles: string[] = existingUser.roleNames || [];
  if (item.roleName && !existingRoles.includes(item.roleName)) {
    existingUser.roleNames = [...existingRoles, item.roleName];
  }
}

function buildMergedUserList(payload: any[]): any[] {
  const mergedUsers: any[] = [];
  const userMap = new Map();
  payload.forEach((item: any) => {
    if (item.groupId || !item.id) {
      mergedUsers.push({ ...item, roleNames: item.roleName ? [item.roleName] : [] });
    } else {
      mergeItemIntoUserMap(userMap, item);
    }
  });
  userMap.forEach(user => mergedUsers.push(user));
  return mergedUsers;
}

function shouldKeepExistingEvent(existingEvent: any, newEvents: any[]): boolean {
  const conflicting = newEvents.find(
    e => e.id === existingEvent.id && e.recursionId === existingEvent.recursionId
  );
  if (!conflicting) return true;
  return !(
    conflicting.searchUserId === existingEvent.searchUserId &&
    conflicting.searchUserType === existingEvent.searchUserType
  );
}

function mergeEventLists(existingEvents: any[], newEvents: any[]): any[] {
  const kept = existingEvents.filter(e => shouldKeepExistingEvent(e, newEvents));
  return [...kept, ...newEvents];
}

function deduplicateEvents(events: any[]): any[] {
  return events.filter(
    (event: any, index: number, self: any[]) =>
      index ===
      self.findIndex(
        (e: any) =>
          e.id === event.id &&
          e.recursionId === event.recursionId &&
          e.searchUserId === event.searchUserId
      )
  );
}

function addParentChildIds(user: any, ids: number[]): void {
  if (user?.type === 'PARENT' && user?.userChildData?.length) {
    user.userChildData.forEach((child: any) => {
      if (child?.childId) ids.push(child.childId);
    });
  }
}

function collectSearchUserIds(searchedUsers: any[]): number[] {
  const ids: number[] = [];
  searchedUsers.forEach((user: any) => {
    const userId = user?.id || user.childId || user?.parentId;
    if (userId) ids.push(userId);
    addParentChildIds(user, ids);
  });
  return ids;
}
interface State {
  getClinicianAndTechNames: any[];
  getUserAndGroupNames: any[];
  scheduledEvents: ScheduledEvent[];
  name: string;
  multipleUsers: any[];
  scheduledEventsProviders: any[];
  clickedUser: Record<string, boolean>;
  colorMapMonth: Record<string, string>;
  colorMapWeek: Record<string, string>;
  searchedUsers: any[];
  colorUsers: Record<string, string>;
  heights: Record<string, any>;
  getAllEmployeesAndChild: any[];
  getTechnicianAvailability: Record<string, { dates: string[]; color: string }>;
  dateChange: boolean;
  availabilityData: any;
  getActiveTechnician: any[];
  appointmentWith: { id: any; type: string } | null;
}
const initialState: State = {
  getClinicianAndTechNames: [],
  getUserAndGroupNames: [],
  scheduledEvents: [],
  name: '',
  multipleUsers: [],
  scheduledEventsProviders: [],
  clickedUser: {},
  colorMapMonth: {},
  colorMapWeek: {},
  searchedUsers: [],
  colorUsers: {},
  heights: {},
  getAllEmployeesAndChild: [],
  availabilityData: [],
  getTechnicianAvailability: {
    user9: {
      dates: [
        "new Date('2024-07-14T08:00:00').toString()",
        "new Date('2024-07-14T08:30:00').toString()",
        "new Date('2024-07-15T09:00:00').toString()",
      ],
      color: 'lightblue',
    },
    user1: {
      dates: [
        "new Date('2024-07-16T09:00:00').toString()",
        "new Date('2024-07-16T09:30:00').toString()",
      ],
      color: 'lightgreen',
    },
    user3: {
      dates: ["new Date('2024-07-17T09:00:00').toString()"],
      color: 'yellow',
    },
  },
  dateChange: false,
  getActiveTechnician: [],
  appointmentWith: null,
};

interface MyDataEvents {
  providerId: any;
  groupId: any;
  childId: any;
  appointmentStartDate: any;
  appointmentEndDate: any;
  appointmentWith?: { id: any; type: string } | null;
}
interface MyDataMultipleUsersEvents {
  userIds: any;
  appointmentStartDate: any;
  appointmentEndDate: any;
  appointmentWith?: { id: any; type: string } | null;
}
interface MyDataUserAndGroupNames {
  organizationId: any;
}
interface MyDataTechnicianNames {
  technicianData: any;
  fromDate: any;
  toDate: any;
}
export const getClinicianAndTechNamesCall = createAsyncThunk(
  'getClinicianAndTechNamesCall',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getClinicianAndTechAPI.getClinicianAndTech();
      return res.data.data;
    } catch (err) {
      console.error(err);
      rejectWithValue('Error in Response');
    }
  }
);
export const getTechnicianAvailibityCall = createAsyncThunk(
  'getTechnicianAvailibityCall',
  async (payload: MyDataTechnicianNames, { rejectWithValue }) => {
    try {
      const res = await getTechnicianAvailabilityAPI.getTechnicianAvailability(payload);
      return res.data.data;
    } catch (err) {
      console.error(err);
      rejectWithValue('Error in Response');
    }
  }
);
export const getAllUserAndGroupNamesCall = createAsyncThunk(
  'getAllUserAndGroupNamesCall',
  async (payload: MyDataUserAndGroupNames, { rejectWithValue }) => {
    try {
      const res = await getAllUserAndGroupNamesAPI.getAllUserAndGroupNames(payload);
      return res.data.data;
    } catch (err) {
      console.error(err);
      rejectWithValue('Error in Response');
    }
  }
);
export const getActiveTechnicianCall = createAsyncThunk(
  'getActiveTechnicianCall',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllUserAndGroupNamesAPI.getAllActiveTechnician();
      return res.data.data;
    } catch (err) {
      console.error(err);
      rejectWithValue('Error in Response');
    }
  }
);
export const getAllEmployeesAndChildCall = createAsyncThunk(
  'getAllEmployeesAndChildCall',
  async (payload: MyDataUserAndGroupNames, { rejectWithValue }) => {
    try {
      const res = await getAllEmployeesAndChildAPI.getAllEmployeesAndChild(payload);
      return res.data.data;
    } catch (err) {
      console.error(err);
      rejectWithValue('Error in Response');
    }
  }
);
export const getScheduleEventCall = createAsyncThunk(
  'getScheduleEventCall',
  async (payload: MyDataEvents, { rejectWithValue }) => {
    try {
      const res = await getScheduleEventAPI.getScheduleEvent(payload);
      return res.data.data;
    } catch (err) {
      console.error(err);
      rejectWithValue('Error in Response');
    }
  }
);
export const getScheduleEventProvidersCall = createAsyncThunk(
  'getScheduleEventProvidersCall',
  async (payload: MyDataMultipleUsersEvents, { rejectWithValue }) => {
    try {
      const res = await getEventsByProvidersAPI.getEventsByProviders(payload);
      return res.data.data;
    } catch (err) {
      console.error(err);
      rejectWithValue('Error in Response');
    }
  }
);
const getServices = createSlice({
  name: 'getServices',
  initialState,
  reducers: {
    savingClinicianAndTechNames: (state, action) => {
      state.getClinicianAndTechNames = { ...action.payload };
    },
    savingUserAndGroupNames: (state, action) => {
      state.getUserAndGroupNames = { ...action.payload };
    },
    savingEmployeesAndChildNames: (state, action) => {
      state.getAllEmployeesAndChild = { ...action.payload };
    },
    setDateChange: (state, action) => {
      state.dateChange = action.payload;
    },
    savingScheduledEvents: (state, action) => {
      const newEvent = action.payload;
      if (!newEvent) return;
      const existingEvent = state.scheduledEvents.find(
        (item: any) => item.searchUserId === newEvent.searchUserId
      );
      if (!existingEvent) {
        state.scheduledEvents = [...state.scheduledEvents, newEvent];
      }
    },
    setProviderChange: (state: any, action: any) => {
      state.scheduledEvents = state.scheduledEvents.map((event: any) =>
        parseInt(event.id) === parseInt(action.payload.id)
          ? { ...event, searchUserId: action.payload.searchUserId }
          : event
      );
    },
    savingSelectedName: (state, action) => {
      state.name = action.payload;
    },
    savingMultipleUsers: (state, action) => {
      const newUser = action.payload;
      const existingUser = state.multipleUsers.find(
        item => getUserIdentifier(item) === getUserIdentifier(newUser)
      );
      if (!existingUser) {
        state.multipleUsers.push(newUser);
      }
    },
    savingSearchedUsers: (state: any, action: any) => {
      const existingUser = findMatchingSearchedUser(state.searchedUsers, action.payload);
      if (!existingUser && (action?.payload?.firstName || action?.payload?.lastName)) {
        state.searchedUsers = [...(state?.searchedUsers || []), action?.payload];
      } else if (existingUser) {
        state.searchedUsers = state.searchedUsers.map((user: any) =>
          applySearchedUserUpdate(user, action.payload)
        );
      }
    },
    removeGroupsFromUsers: (state: any) => {
      state.searchedUsers = state.searchedUsers.map((user: any) => {
        delete user.group;
        return user;
      });
    },
    savingSearchedUsersForGroup: (state: any, action: any) => {
      const existingUser = findMatchingSearchedUser(state.searchedUsers, action.payload);
      if (!existingUser && (action?.payload?.firstName || action?.payload?.lastName)) {
        state.searchedUsers = [...(state?.searchedUsers || []), action?.payload];
      } else if (existingUser) {
        state.searchedUsers = state.searchedUsers.map((user: any) =>
          applyGroupUserUpdate(user, action.payload)
        );
      }
    },
    savingTechnicianAvailability: (state: any, action: any): any => {
      const existingName1 = state.availabilityData.find(
        (item: any) => parseInt(item.id) === parseInt(action?.payload?.id)
      );
      if (!existingName1) {
        const searchUserIds = state.searchedUsers.map((user: any) => user.id);
        state.availabilityData = [...state.availabilityData, action.payload].filter((data: any) =>
          searchUserIds.includes(data.searchUserId)
        );
      }
    },
    updateAvailabilityData: (state: any, action: any): any => {
      state.availabilityData = [...action.payload];
    },
    deleteSearchedUser: (state: any, action: any) => {
      state.searchedUsers = state.searchedUsers?.filter(
        (item: any) =>
          !(
            (item.id || item?.groupId || item?.childId || item?.parentId) === action.payload?.id &&
            item?.type === action.payload?.type
          )
      );
      if (!state.searchedUsers?.length) {
        state.appointmentWith = null;
      }
    },
    deleteAllSearchedUser: (state: any, action: any) => {
      state.searchedUsers = state.searchedUsers?.filter(
        (item: any) =>
          (item.id || item?.groupId || item?.childId || item?.parentId) === action.payload?.id
      );
      state.appointmentWith = null;
    },
    setAppointmentWith: (state: any, action: any) => {
      state.appointmentWith = action.payload;
    },
    clearAppointmentWith: (state: any) => {
      state.appointmentWith = null;
    },
    deleteUser: (state: any, action: any) => {
      state.multipleUsers = state.multipleUsers?.filter(
        (item: any) =>
          (item.id || item?.groupId || item?.childId || item?.parentId) !== action.payload
      );
    },
    deleteUserEvent: (state: any, action: any) => {
      state.scheduledEvents = state.scheduledEvents?.filter(
        (item: any) =>
          !(item.searchUserId === action.payload.id && item.searchUserType === action.payload?.type)
      );
      // Don't delete availability data - it will be filtered by clickedUser state
      // This allows availability to reappear when user checks the checkbox back
    },
    deleteUserAvailability: (state: any, action: any) => {
      // Delete availability data when user is completely removed (X button)
      state.availabilityData = state.availabilityData?.filter(
        (item: any) => item.searchUserId !== action.payload.id
      );
    },
    deleteAllUserEvent: (state: any, action: any) => {
      state.scheduledEvents = state.scheduledEvents?.filter(
        (item: any) => item.searchUserId === action.payload
      );
      state.availabilityData = state.availabilityData?.filter(
        (item: any) => item.searchUserId === action.payload
      );
    },
    deleteSingleEvent: (state: any, action: any) => {
      state.scheduledEvents = state.scheduledEvents?.filter(
        (item: any) => item.id !== action.payload
      );
    },
    savingClickedUser: (state: any, action: any) => {
      state.clickedUser = {
        ...state.clickedUser,
        [action.payload]: !state.clickedUser[action.payload],
      };
    },
    savingInitialUser: (state: any, action: any) => {
      state.clickedUser = {
        ...state.clickedUser,
        [action.payload]: true,
      };
    },
    savingInitialUsers: (state: any, action: any) => {
      state.clickedUser = {
        ...state.clickedUser,
        ...action.payload,
      };
    },
    deleteClickedUser: (state: any, action: any) => {
      delete state.clickedUser[action.payload];
    },
    savingColorMapMonth: (state: any, action: any) => {
      state.colorMapMonth = {
        ...state.colorMapMonth,
        ...action.payload,
      };
    },
    deleteColorMapMonth: (state: any, action: any) => {
      delete state.colorMapMonth[action?.payload];
    },
    savingColorMapWeek: (state: any, action: any) => {
      state.colorMapWeek = {
        ...state.colorMapWeek,
        ...action.payload,
      };
    },
    deleteColorMapWeek: (state: any, action: any) => {
      delete state.colorMapWeek[action?.payload];
    },
    savingColorUsers: (state: any, action: any) => {
      state.colorUsers = {
        ...state.colorUsers,
        ...action.payload,
      };
    },
    deleteColorUsers: (state: any, action: any) => {
      delete state.colorUsers[action?.payload];
    },
    savingHeights: (state: any, action: any) => {
      state.heights = {
        ...state.heights,
        ...action.payload,
      };
    },
    deleteHeights: (state: any, action: any) => {
      delete state.heights[action?.payload];
    },
    clearUser: (state: any) => {
      state.multipleUsers = [];
      state.scheduledEvents = [];
      state.clickedUser = {};
      state.colorMapMonth = {};
      state.colorUsers = {};
      state.searchedUsers = [];
      state.colorMapWeek = {};
      state.heights = {};
    },
    clearEvents: (state: any) => {
      state.scheduledEvents = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getClinicianAndTechNamesCall.pending, () => {})
      .addCase(getClinicianAndTechNamesCall.fulfilled, (state: any, action) => {
        state.getClinicianAndTechNames = action.payload;
      })
      .addCase(getTechnicianAvailibityCall.fulfilled, (state: any, action) => {
        if (!Array.isArray(state.getTechnicianAvailability)) {
          state.getTechnicianAvailability = [];
        } else {
          state.getTechnicianAvailability = [...state.getTechnicianAvailability, ...action.payload];
        }
      })
      .addCase(getAllUserAndGroupNamesCall.fulfilled, (state: any, action) => {
        state.getUserAndGroupNames = buildMergedUserList(action.payload);
      })
      .addCase(getActiveTechnicianCall.fulfilled, (state: any, action) => {
        state.getActiveTechnician = action.payload;
      })
      .addCase(getAllEmployeesAndChildCall.fulfilled, (state: any, action) => {
        state.getAllEmployeesAndChild = action.payload;
      })
      .addCase(getScheduleEventCall.fulfilled, (state: any, action) => {
        if (!Array.isArray(action.payload)) return;
        const arg = action.meta.arg as any;
        if (arg.appointmentWith) {
          // Replace stale events for this provider when an appointmentWith filter is active
          const requestedId = arg.providerId || arg.groupId || arg.childId;
          const otherEvents = requestedId
            ? state.scheduledEvents.filter((e: any) => e.searchUserId !== requestedId)
            : state.scheduledEvents;
          state.scheduledEvents = deduplicateEvents([...otherEvents, ...action.payload]);
        } else {
          const merged = mergeEventLists(state.scheduledEvents, action.payload);
          state.scheduledEvents = deduplicateEvents(merged);
        }
      })
      .addCase(getScheduleEventProvidersCall.fulfilled, (state: any, action) => {
        if (!Array.isArray(action.payload)) return;
        const arg = action.meta.arg as any;
        const searchUserIds = collectSearchUserIds(state.searchedUsers);
        if (arg.appointmentWith) {
          // Replace stale events for the requested users when an appointmentWith filter is active
          const requestedUserIds = (arg.userIds || []).map((u: any) => u.id ?? u);
          const otherEvents = state.scheduledEvents.filter(
            (e: any) => !requestedUserIds.includes(e.searchUserId)
          );
          state.scheduledEvents = deduplicateEvents([...otherEvents, ...action.payload]).filter(
            (e: any) => searchUserIds.includes(e.searchUserId)
          );
        } else {
          const merged = mergeEventLists(state.scheduledEvents, action.payload);
          const deduped = deduplicateEvents(merged);
          state.scheduledEvents = deduped.filter((e: any) =>
            searchUserIds.includes(e.searchUserId)
          );
        }
      })
      .addCase(getClinicianAndTechNamesCall.rejected, () => {});
  },
});
// Action creators are generated for each case reducer function
export const {
  savingClinicianAndTechNames,
  savingScheduledEvents,
  savingSelectedName,
  savingMultipleUsers,
  deleteUser,
  deleteUserEvent,
  savingClickedUser,
  savingColorMapMonth,
  clearUser,
  savingUserAndGroupNames,
  savingSearchedUsers,
  savingColorUsers,
  deleteColorUsers,
  deleteColorMapMonth,
  deleteSearchedUser,
  deleteClickedUser,
  savingEmployeesAndChildNames,
  savingTechnicianAvailability,
  setDateChange,
  savingColorMapWeek,
  deleteColorMapWeek,
  savingHeights,
  deleteHeights,
  deleteSingleEvent,
  setProviderChange,
  deleteAllSearchedUser,
  deleteAllUserEvent,
  clearEvents,
  updateAvailabilityData,
  savingInitialUser,
  savingSearchedUsersForGroup,
  removeGroupsFromUsers,
  savingInitialUsers,
  deleteUserAvailability,
  setAppointmentWith,
  clearAppointmentWith,
} = getServices.actions;
export default getServices.reducer;
